// Hợp đồng của commission-service (đặc tả docs/backend/services/commission-service.md): sổ hoa hồng theo giao dịch
// (dealCommissionLines của packages/domain), kỳ chi trả nháp → duyệt → đã chi, saga duyệt kỳ (chốt bảng công ở
// attendance), thu hồi (sagas.md SG-5), chuyển trừ phần thực nhận âm, phiếu thu nhập.
// Quy ước: tiền VND số nguyên (number — có thể âm ở thu hồi/thực nhận); kỳ = tháng YYYY-MM (VN); id UUID.
// Payload sự kiện không chứa lương cơ bản / phụ cấp / lương BHXH của từng người.
import { z } from 'zod';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const reqText = (max: number, message: string) => str(message).trim().min(1, message).max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const int = () => z.number({ required_error: REQUIRED, invalid_type_error: 'Phải là số' }).int('Phải là số nguyên');
const period = str('Chọn kỳ chi trả').regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ chi trả không hợp lệ');

// ---------- Hằng số (nhãn vai trò ở packages/domain COMM_ROLE) ----------
export const COMMISSION_ROLES = ['sale', 'hot', 'leader', 'manager', 'referral'] as const;
export type CommissionRole = (typeof COMMISSION_ROLES)[number];
export const PAYOUT_STATUSES = ['draft', 'approved', 'paid'] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];
export const PAYOUT_SOURCE_KINDS = ['ledger', 'timesheet', 'kpi', 'salary', 'policy', 'staff', 'carryForward'] as const;
export type PayoutSourceKind = (typeof PAYOUT_SOURCE_KINDS)[number];
export const APPROVAL_FAIL_REASONS = ['TIMESHEET_CHANGED', 'TIMESHEET_TIMEOUT', 'TIMESHEET_NOT_FINALIZABLE'] as const;
export type ApprovalFailReason = (typeof APPROVAL_FAIL_REASONS)[number];
/**
 * Thông điệp mặc định theo lý do duyệt thất bại: commission.payout.approvalFailed.message (khi không có thông điệp riêng) và
 * PayoutSaved.lastApprovalFailure ở màn "Kỳ chi trả".
 */
export const APPROVAL_FAIL_MESSAGE: Readonly<Record<ApprovalFailReason, string>> = {
  TIMESHEET_CHANGED: 'Bảng công tháng đã chốt khác bản nháp — bấm "Tính lại bản nháp" rồi duyệt lại',
  TIMESHEET_TIMEOUT: 'Chưa chốt được bảng công tháng (hết thời gian chờ) — thử duyệt lại sau',
  TIMESHEET_NOT_FINALIZABLE: 'Không chốt được bảng công tháng — kiểm tra lại kỳ chấm công',
};
export const CARRY_FORWARD_STATUSES = ['open', 'applied', 'settled'] as const;
export type CarryForwardStatus = (typeof CARRY_FORWARD_STATUSES)[number];
export const CARRY_FORWARD_RESOLUTIONS = ['repaid', 'waived'] as const;
export type CarryForwardResolution = (typeof CARRY_FORWARD_RESOLUTIONS)[number];
/** Dòng kỳ có |amount| < 1.000 bị bỏ (R1). */
export const MIN_PAYOUT_LINE = 1000;
/**
 * Khoá idempotency saga duyệt kỳ (§2.6). Lần chạy đầu của một revision: '{payoutId}:finalize:{revision}'; duyệt lại cùng
 * revision sau khi saga trước kết thúc (A4 hết hạn / bị từ chối) dùng khoá riêng '…:{run}' (run ≥ 2) — attendance phát lại
 * phản hồi đã lưu theo khoá nên khoá cũ không dùng lại được. Bên nhận coi khoá là chuỗi mờ.
 */
export const payoutKeys = {
  finalize: (payoutId: string, revision: number, run = 1) => `${payoutId}:finalize:${revision}${run > 1 ? `:${run}` : ''}`,
  release: (payoutId: string, revision: number, run = 1) => `${payoutId}:release:${revision}${run > 1 ? `:${run}` : ''}`,
};

// ---------- Lệnh ----------
export const periodRef = z.object({ period });
/** Chốt / tính lại bản nháp (payout.manage). */
export const draftPayoutInput = z.object({ period, expectedRevision: int().min(0, 'Phiên bản không hợp lệ').optional() });
/** Duyệt kỳ (payout.approve). If-Match = revision bản nháp. 200 { state: approved } hoặc 202 { state: finalizing, sagaId }. */
export const approvePayoutInput = z.object({ period, expectedRevision: int().min(1, 'Phiên bản không hợp lệ') });
export const markPayoutPaidInput = periodRef;
/** Trả text/csv thu-nhap-{period}.csv. */
export const exportPayoutCsvInput = periodRef;
export const settleCarryForwardInput = z.object({
  carryForwardId: uuid,
  resolution: z.enum(CARRY_FORWARD_RESOLUTIONS, msg('Chọn cách tất toán')),
  note: reqText(500, 'Nhập ghi chú tất toán'),
});

// ---------- Truy vấn công khai ----------
export const getMyCommissionInput = z.object({});
export const getMyCommissionSummaryInput = z.object({});
export const listCommissionLedgerInput = z.object({
  staffId: uuid.optional(),
  /** due = chỉ dòng còn phải chi / thu hồi (|due| ≥ 1.000). */
  only: z.enum(['due', 'all'], msg('Bộ lọc không hợp lệ')).default('due'),
});
export const getDealCommissionLinesInput = z.object({ dealId: uuid });
export const getPayoutPeriodInput = periodRef;
export const getPayslipInput = z.object({ period, staffId: uuid });
export const getCommissionPolicyInput = z.object({});
export const getCommissionPipelineSummaryInput = z.object({});
export const listCarryForwardsInput = z.object({ status: z.enum(CARRY_FORWARD_STATUSES, msg('Trạng thái không hợp lệ')).optional(), staffId: uuid.optional() });

// ---------- Lệnh nội bộ (vận hành — chỉ service principal, không qua gateway) ----------
/** Đối soát mirror (identity / inventory / hr / deal) rồi dựng lại sổ + kiểm paid_ledger (§3.2, §10.3 ledger-reconcile). */
export const reconcileLedgerInput = z.object({});
/** Dựng lại commission_line từ mirror + sửa paid_ledger lệch Σ payout_line (§3.2). */
export const rebuildLedgerInput = z.object({});
export interface RebuildLedgerResult {
  paidLedgerFixed: number;
  linesChanged: number;
  clawbacks: number;
}
export interface ReconcileLedgerResult extends RebuildLedgerResult {
  /** Số bản ghi đã đối soát theo nguồn; -1 = nguồn không phản hồi (bỏ qua). */
  synced: { policy: number; projects: number; teams: number; staff: number; deals: number };
}

// ---------- Truy vấn nội bộ ----------
export const getPayoutStatusesInput = z.object({ months: z.array(period).min(1, 'Chọn ít nhất một tháng').max(36, 'Tối đa 36 tháng') });

/** Tên RPC: commission.cmd.<Name> / commission.qry.<Name>. */
export const RPC = {
  cmd: [
    'DraftPayout',
    'ApprovePayout',
    'MarkPayoutPaid',
    'ExportPayoutCsv',
    'SettleCarryForward',
    // nội bộ (vận hành)
    'ReconcileLedger',
    'RebuildLedger',
  ],
  qry: [
    'GetMyCommission',
    'GetMyCommissionSummary',
    'ListCommissionLedger',
    'GetDealCommissionLines',
    'GetPayoutPeriod',
    'GetPayslip',
    'GetCommissionPolicy',
    'GetCommissionPipelineSummary',
    'ListCarryForwards',
    // nội bộ
    'GetPayoutStatuses',
  ],
} as const;

// ---------- DTO / read model ----------
export interface CommissionLineDto {
  /** '<dealId>:<role>:<staffId>'. */
  key: string;
  dealId: string;
  dealCode: string | null;
  unitCode: string | null;
  projectName: string | null;
  dealStatus: string;
  dealCreatedAt: string;
  confirmedAt: string | null;
  staffId: string;
  staffName: string | null;
  role: CommissionRole;
  /** null ⇒ "cố định" (thưởng nóng). */
  pct: number | null;
  fee: number;
  /** Tiến độ mốc 0–1. */
  ratio: number;
  gross: number;
  eligible: number;
  paid: number;
  due: number;
  cancelled: boolean;
}
export interface MyCommission {
  summary: { gross: number; eligible: number; paid: number; due: number };
  lines: CommissionLineDto[];
  slips: { period: string; status: Exclude<PayoutStatus, 'draft'>; net: number }[];
}
export interface MyCommissionSummary {
  eligible: number;
  due: number;
}
export interface CommissionLedger {
  lines: CommissionLineDto[];
  count: number;
  dueTotal: number;
  recipients: { staffId: string; name: string }[];
}
export interface DealCommissionLines {
  /** commission.view ∨ có dòng của người xem. */
  visible: boolean;
  fee: number;
  ratio: number;
  milestonesReached: { type: 'deposit' | 'contract' | 'paidPct' | 'devPaid'; threshold?: number | null; label: string }[];
  lines: { staffId: string; staffName: string | null; role: CommissionRole; pct: number | null; gross: number; eligible: number }[];
}
/** Dòng bảng thu nhập (R14). */
export interface PayoutRowDto {
  staffId: string;
  staffCode: string | null;
  staffName: string;
  contractType: 'official' | 'probation' | 'ctv';
  credit: number;
  std: number;
  baseSalary: number;
  allowance: number;
  commission: number;
  /** ≤ 0. */
  clawback: number;
  kpi: number;
  gtgd: number;
  target: number;
  penalty: number;
  lateCount: number;
  lateFree: number;
  insurance: number;
  insurancePct: number;
  tax: number;
  taxPct: number;
  carryIn: number;
  net: number;
}
export interface PayoutTotals {
  baseSalary: number;
  allowance: number;
  commission: number;
  clawback: number;
  kpi: number;
  penalty: number;
  insurance: number;
  tax: number;
  carryIn: number;
  net: number;
}
export interface PayoutSaved {
  payoutId: string;
  status: PayoutStatus;
  revision: number;
  /**
   * Tổng thực nhận của bản đang lưu ở cùng mức xem lương với `rows` (R16): người không đọc được lương (salary.manage ∨
   * payout.manage) ⇒ Σ thực nhận đã che lương (ảnh chụp approved/paid và bản nháp); ngược lại tổng đã lưu.
   */
  total: number;
  rowCount: number;
  createdAt: string;
  draftedBy: string;
  updatedAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  paidBy: string | null;
  paidAt: string | null;
  approval: { state: 'finalizing'; requestedAt: string } | null;
  staleAtApproval: boolean | null;
  /**
   * Lần duyệt gần nhất của bản nháp hiện tại (cùng revision) thất bại — saga chốt bảng công kết thúc không duyệt (A2' / A4),
   * không có saga mới đang chạy; `at` = lúc thất bại. Kỳ đã duyệt / đã chi, đang duyệt, chưa từng thất bại hoặc bản nháp đã
   * tính lại ⇒ null. Mọi người xem kỳ đều thấy (không còn chỉ qua thông báo gửi người duyệt). Tuỳ chọn để bên giả lập cũ vẫn
   * hợp lệ; commission luôn điền. `message` = đúng thông điệp của commission.payout.approvalFailed lần đó (VD TIMESHEET_CHANGED
   * vì nền "đã chi" đổi giữa lúc bấm duyệt và lúc chốt bảng công có câu riêng, không phải APPROVAL_FAIL_MESSAGE) — web ưu tiên
   * `message`; thiếu (bên giả lập cũ) ⇒ APPROVAL_FAIL_MESSAGE[reasonCode].
   */
  lastApprovalFailure?: { reasonCode: ApprovalFailReason; at: string; message?: string } | null;
}
export interface PayoutPeriodView {
  period: string;
  serverTime: string;
  saved: PayoutSaved | null;
  /** approved/paid ⇒ snapshot; chưa có hoặc draft ⇒ live. */
  view: 'snapshot' | 'live';
  rows: PayoutRowDto[];
  totals: PayoutTotals;
  rowCount: number;
  liveTotal?: number;
  stale: boolean;
  sourcesChanged?: { at: string; kinds: PayoutSourceKind[] } | null;
  salaryAvailable: boolean;
  actions: { canDraft: boolean; canApprove: boolean; canMarkPaid: boolean; canExport: boolean };
}
export interface Payslip {
  period: string;
  status: PayoutStatus | 'live';
  view: 'snapshot' | 'live';
  staff: { name: string; code: string | null; contractType: 'official' | 'probation' | 'ctv' };
  /** null ⇒ "Không có dữ liệu" (không đủ quyền hoặc không có dòng — không phân biệt). */
  row: PayoutRowDto | null;
  lines: { key: string; dealId: string; unitCode: string | null; role: CommissionRole; eligible: number; paidBefore: number; amount: number }[];
  labels: { lateFree: number; insurancePct: number; taxPct: number };
}
export interface CommissionPolicyView {
  settingsVersion: number;
  commission: {
    onPreVAT: boolean;
    share: { official: number; probation: number; ctv: number };
    tiers: { minDeals: number; bonusPct: number }[];
    leaderPct: number;
    managerPct: number;
    referralPct: number;
    kpiBonus: { minPct: number; amount: number }[];
    tax: { official: number; probation: number; ctv: number };
    taxThreshold: number;
    insurancePct: number;
  };
  attendance: { lateFree: number; latePenalty: number };
  actions: { canEditPolicy: boolean };
}
export interface CommissionPipelineSummary {
  /** Σ max(0, eligible − paid) toàn tenant. */
  dueTotal: number;
}
export interface CarryForwardView {
  id: string;
  staffId: string;
  staffName: string | null;
  sourcePeriod: string;
  amount: number;
  status: CarryForwardStatus;
  appliedPeriod: string | null;
  resolution: CarryForwardResolution | null;
  note: string | null;
  settledAt: string | null;
  settledBy: string | null;
}
export interface DraftPayoutResult {
  payoutId: string;
  revision: number;
  total: number;
  rowCount: number;
  salaryAvailable: boolean;
}
export type ApprovePayoutResult = { state: 'approved'; payoutId: string } | { state: 'finalizing'; payoutId: string; sagaId: string };
export interface MarkPayoutPaidResult {
  status: 'paid';
  payoutId: string;
}
export interface SettleCarryForwardResult {
  status: 'settled';
  carryForwardId: string;
  payoutBasisVersion: number;
}
/** Kết quả ExportPayoutCsv — gateway trả text/csv, Content-Disposition: attachment; filename="thu-nhap-{period}.csv". */
export interface PayoutCsvFile {
  fileName: string;
  contentType: string;
  /** CSV (BOM UTF-8, phân cách ',', thoát '"' như toCSV). */
  content: string;
  rowCount: number;
}
// Nội bộ
export interface PayoutStatusItem {
  month: string;
  status: 'none' | PayoutStatus;
  version: number;
  /**
   * Kỳ đã chốt: payoutId / tổng / revision của bản đang lưu (chưa chốt ⇒ null / 0 / 0) — notification đối soát dựng lại mục "kỳ chi
   * trả chờ duyệt" khi lỡ commission.payout.drafted / recalculated. Tuỳ chọn để bên giả lập cũ vẫn hợp lệ; commission luôn điền.
   */
  payoutId?: string | null;
  total?: number;
  revision?: number;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  payoutDrafted: 'commission.payout.drafted',
  payoutRecalculated: 'commission.payout.recalculated',
  // Saga duyệt kỳ chi trả (commission điều phối, attendance tham gia)
  payoutTimesheetFinalizeRequested: 'commission.payout.timesheetFinalizeRequested',
  payoutTimesheetReleaseRequested: 'commission.payout.timesheetReleaseRequested',
  payoutApproved: 'commission.payout.approved',
  payoutApprovalFailed: 'commission.payout.approvalFailed',
  payoutPaid: 'commission.payout.paid',
  payoutExported: 'commission.payout.exported',
  clawbackScheduled: 'commission.clawback.scheduled',
  carryForwardCreated: 'commission.carryForward.created',
  carryForwardApplied: 'commission.carryForward.applied',
  carryForwardSettled: 'commission.carryForward.settled',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** aggregateId = payoutId. */
export interface PayoutDrafted {
  payoutId: string;
  period: string;
  revision: number;
  total: number;
  rowCount: number;
  lineCount: number;
  clawbackTotal: number;
  policyVersion: number;
  timesheet: { periodStatus: 'open' | 'finalized'; periodRevision: number };
  salaryAvailable: boolean;
  draftedBy: string;
}
export interface PayoutRecalculated extends PayoutDrafted {
  previousRevision: number;
  previousTotal: number;
}
/** Bước A1: yêu cầu attendance chốt bảng công tháng (thử lại = sự kiện mới, cùng khoá, attempt + 1). */
export interface PayoutTimesheetFinalizeRequested {
  idempotencyKey: string; // '<payoutId>:finalize:<revision>' (lần chạy ≥ 2 của cùng revision: '…:<run>')
  attempt: number;
  payoutId: string;
  month: string;
  requestedBy: string;
}
/** Bù trừ A4: mở lại bảng công nếu chính khoá chốt này đã chốt và kỳ chưa duyệt. */
export interface PayoutTimesheetReleaseRequested {
  idempotencyKey: string; // khoá chốt gốc
  releaseKey: string; // '<payoutId>:release:<revision>' (lần chạy ≥ 2: '…:<run>')
  attempt: number;
  payoutId: string;
  month: string;
  reason: string;
}
/** Pivot A3 — thao tác nhạy cảm "Duyệt kỳ chi trả". */
export interface PayoutApproved {
  payoutId: string;
  period: string;
  revision: number;
  total: number;
  rowCount: number;
  lineCount: number;
  staffIds: string[];
  approvedBy: string;
  approvedAt: string;
  timesheetFinalized: boolean;
  timesheetRevision: number | null;
  staleAtApproval: boolean;
  payoutBasisVersion: number;
}
export interface PayoutApprovalFailed {
  payoutId: string;
  period: string;
  revision: number;
  reasonCode: ApprovalFailReason;
  message: string;
  diffs?: { staffId: string; field: 'credit' | 'std' | 'lateCount' | 'penalty' | 'missingRow' }[];
  requestedBy: string;
}
export interface PayoutPaid {
  payoutId: string;
  period: string;
  total: number;
  rowCount: number;
  staffIds: string[];
  paidBy: string;
  paidAt: string;
}
/** Thao tác nhạy cảm "Xuất bảng thu nhập". */
export interface PayoutExported {
  payoutId: string | null;
  period: string;
  view: 'snapshot' | 'live';
  rowCount: number;
  fileName: string;
  exportedBy: string;
}
/** aggregateId = key dòng. Khử trùng theo (key, eligible, paid). due < 0. */
export interface ClawbackScheduled {
  key: string;
  dealId: string;
  dealCode: string | null;
  unitCode: string | null;
  staffId: string;
  role: CommissionRole;
  eligible: number;
  paid: number;
  due: number;
  dealStatus: string;
  cause: 'dealCancelled' | 'recalculated';
}
/** aggregateId = carryForwardId. */
export interface CarryForwardCreated {
  carryForwardId: string;
  staffId: string;
  sourcePeriod: string;
  amount: number;
}
export interface CarryForwardApplied {
  carryForwardId: string;
  staffId: string;
  sourcePeriod: string;
  appliedPeriod: string;
  amount: number;
}
export interface CarryForwardSettled {
  carryForwardId: string;
  staffId: string;
  sourcePeriod: string;
  amount: number;
  resolution: CarryForwardResolution;
  note: string;
  settledBy: string;
}

export interface EventPayloads {
  [EVENTS.payoutDrafted]: PayoutDrafted;
  [EVENTS.payoutRecalculated]: PayoutRecalculated;
  [EVENTS.payoutTimesheetFinalizeRequested]: PayoutTimesheetFinalizeRequested;
  [EVENTS.payoutTimesheetReleaseRequested]: PayoutTimesheetReleaseRequested;
  [EVENTS.payoutApproved]: PayoutApproved;
  [EVENTS.payoutApprovalFailed]: PayoutApprovalFailed;
  [EVENTS.payoutPaid]: PayoutPaid;
  [EVENTS.payoutExported]: PayoutExported;
  [EVENTS.clawbackScheduled]: ClawbackScheduled;
  [EVENTS.carryForwardCreated]: CarryForwardCreated;
  [EVENTS.carryForwardApplied]: CarryForwardApplied;
  [EVENTS.carryForwardSettled]: CarryForwardSettled;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
